import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Navigate, Outlet } from "react-router-dom";
import { usePartnerAuth } from "../../../auth/PartnerAuthProvider";
import { useAuth } from "../../../auth/useAuth";
import { usePartnerPortalBase } from "../../partner/PartnerPortalBaseProvider";
import { getAutoMapaAdminPortalConfig } from "../../../constants/automapAdminPortalConfig";
import AutoMapaPartnerPortalLocaleControls from "../../partner/AutoMapaPartnerPortalLocaleControls";
import "../../../styles/automapAdminPortal.css";

function partnerInitials(partner) {
  const source = partner?.displayName || partner?.businessName || partner?.email || "P";
  const parts = String(source).trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return String(source).slice(0, 2).toUpperCase();
}

export default function AutoMapaAdminPortalLayout() {
  const { partner, loading, logout, isAuthenticated } = usePartnerAuth();
  const { user, loading: platformAuthLoading, isSuperAdmin } = useAuth();
  const { loginPath, portalLabel, portalLoadingLabel, basePath } = usePartnerPortalBase();
  const config = getAutoMapaAdminPortalConfig();
  const canAccessPortal = isAuthenticated || isSuperAdmin;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const navItems = useMemo(() => {
    const items = config.navItems;
    if (partner?.features?.whatsappMonitoring === false) {
      return items.filter((item) => item.feature !== "whatsappMonitoring");
    }
    return items;
  }, [config.navItems, partner?.features?.whatsappMonitoring]);

  const { primaryItems, secondaryItems } = useMemo(() => {
    const primary = navItems.filter((item) => item.navGroup !== "secondary");
    const secondary = navItems.filter((item) => item.navGroup === "secondary");
    return { primaryItems: primary, secondaryItems: secondary };
  }, [navItems]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  if (loading || platformAuthLoading) {
    return (
      <div className="automap-admin-portal">
        <p className="automap-admin-portal__loading muted">{portalLoadingLabel}</p>
      </div>
    );
  }

  if (!canAccessPortal) {
    return <Navigate to={loginPath} replace />;
  }

  const displayName =
    partner?.displayName ||
    partner?.businessName ||
    user?.displayName ||
    (isSuperAdmin ? "Platform administrator" : "Admin");
  const accountEmail = partner?.email || user?.email || "";

  return (
    <div className="automap-admin-portal">
      <div className="automap-admin-portal__shell">
        <aside className="automap-admin-portal__sidebar">
          <Link className="automap-admin-portal__brand" to={`${basePath}/dashboard`}>
            <span className="automap-admin-portal__brand-mark" aria-hidden="true">
              AM
            </span>
            <span className="automap-admin-portal__brand-copy">
              <strong>{portalLabel}</strong>
              <span>Internal workspace</span>
            </span>
          </Link>

          <nav className="automap-admin-portal__nav" aria-label={config.portalNavAriaLabel}>
            <p className="automap-admin-portal__nav-label">Workspace</p>
            {primaryItems.map((item) => (
              <NavLink
                key={item.segment}
                to={`${basePath}/${item.segment}`}
                end={item.end}
                className={({ isActive }) =>
                  `automap-admin-portal__nav-link${isActive ? " is-active" : ""}`
                }
              >
                <span className="automap-admin-portal__nav-icon" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            ))}

            {secondaryItems.length > 0 ? (
              <>
                <p className="automap-admin-portal__nav-label automap-admin-portal__nav-label--secondary">
                  More
                </p>
                {secondaryItems.map((item) => (
                  <NavLink
                    key={item.segment}
                    to={`${basePath}/${item.segment}`}
                    className={({ isActive }) =>
                      `automap-admin-portal__nav-link${isActive ? " is-active" : ""}`
                    }
                  >
                    <span className="automap-admin-portal__nav-icon" aria-hidden="true">
                      {item.icon}
                    </span>
                    {item.label}
                  </NavLink>
                ))}
              </>
            ) : null}
          </nav>

          <div className="automap-admin-portal__sidebar-footer">
            <div className="automap-admin-portal__account" ref={menuRef}>
              <button
                type="button"
                className="automap-admin-portal__account-trigger"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span className="automap-admin-portal__account-avatar">
                  {partnerInitials(partner || { displayName })}
                </span>
                <span className="automap-admin-portal__account-meta">
                  <strong>{displayName}</strong>
                  <small>{accountEmail}</small>
                </span>
              </button>
              {menuOpen ? (
                <div className="automap-admin-portal__account-menu" role="menu">
                  <div className="automap-admin-portal__account-menu-prefs">
                    <p className="automap-admin-portal__account-menu-prefs-label">
                      Language &amp; currency
                    </p>
                    <AutoMapaPartnerPortalLocaleControls variant="menu" />
                  </div>
                  <div className="automap-admin-portal__account-menu-divider" aria-hidden="true" />
                  <Link
                    className="automap-admin-portal__account-menu-link"
                    to={`${basePath}/profile`}
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    Account profile
                  </Link>
                  <Link
                    className="automap-admin-portal__account-menu-link"
                    to="/"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    Back to map
                  </Link>
                  <button
                    type="button"
                    className="automap-admin-portal__account-signout"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      if (isAuthenticated) {
                        logout();
                      }
                    }}
                  >
                    {isAuthenticated ? "Sign out" : "Close menu"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </aside>

        <main className="automap-admin-portal__main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
